import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { objectId } from 'modules/menu/options/utils';

import ExistingImage from './ExistingImage';
import MultiImageDndContext from './MultiImageDndContext';
import MultiImageFileSelector, { MultiImageFileError } from './MultiImageFileSelector';
import NewImage from './NewImage';
import UploadErrorsAlert from './UploadErrorsAlert';
import { Crop } from '../ImageCropper';
import { DEFAULT_CROP } from '../constants';

export type Props = {
  className?: string;
  imageCountLimit: number;
  onChange: (value: MultiImageValue[]) => void;
  value: MultiImageValue[];
};

export type MultiImageValue =
  | {
      id: string;
      type: 'existing-image';
      imageUrl: string;
      downloadUrl?: string;
    }
  | {
      id: string;
      type: 'new-image';
      file: File;
      crop: Crop;
    };

const Root = styled.div``;

const MultiImageUploadInput = ({ className, imageCountLimit, onChange, value }: Props) => {
  const [cropping, setCropping] = useState(false);
  const [errors, setErrors] = useState<MultiImageFileError[]>([]);

  const handleSelectFiles = useCallback(
    (files: File[]) => {
      const addedFiles = files.map(file => ({
        id: objectId(),
        crop: DEFAULT_CROP,
        file,
        type: 'new-image' as const,
      }));

      onChange([...value, ...addedFiles]);
    },
    [onChange, value],
  );

  const handleRemoveImage = useCallback(
    (imageId: string) => {
      setErrors([]);
      onChange(value.filter(v => v.id !== imageId));
    },
    [onChange, value],
  );

  const handleCropChange = useCallback(
    (crop: Crop, image: MultiImageValue) => {
      onChange(value.map(v => (v.id === image.id ? { ...image, crop } : v)));
      setCropping(false);
    },
    [onChange, value],
  );

  const handleCropStart = useCallback(() => {
    setErrors([]);
    setCropping(true);
  }, []);

  const handleCropCancel = useCallback(() => {
    setErrors([]);
    setCropping(false);
  }, []);

  return (
    <Root className={className}>
      <MultiImageDndContext onUpdateImageOrder={onChange} originalList={value}>
        {({ displayImageList }) =>
          displayImageList.map(image =>
            image.type === 'existing-image' ? (
              <ExistingImage
                key={image.id}
                imageUrl={image.imageUrl}
                imageId={image.id}
                interactionsDisabled={cropping === true}
                downloadUrl={image.downloadUrl}
                onRemove={handleRemoveImage}
              />
            ) : (
              <NewImage
                key={image.id}
                image={image}
                interactionsDisabled={cropping === true}
                onCropChange={handleCropChange}
                onRemove={handleRemoveImage}
                onStartCropping={handleCropStart}
                onCancelCropping={handleCropCancel}
              />
            ),
          )
        }
      </MultiImageDndContext>
      <MultiImageFileSelector
        imageCountLimit={imageCountLimit}
        currentImageCount={value.length}
        onSelectMultipleFiles={handleSelectFiles}
        onSetErrors={setErrors}
      />
      {errors.length > 0 && <UploadErrorsAlert errors={errors} countLimit={imageCountLimit} />}
    </Root>
  );
};

export default MultiImageUploadInput;
